import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';

// Drama Management Page - v2 version
const DramaManagementPageV2 = () => {
  console.log('=== DramaManagementPageV2 rendered ===');
  const [dramas, setDramas] = useState([]);
  const [filteredDramas, setFilteredDramas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDrama, setEditingDrama] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dramaToDelete, setDramaToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    poster: '',
    episodes: []
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  // Filter dramas
  useEffect(() => {
    let result = [...dramas];

    if (searchTerm) {
      result = result.filter(drama =>
        drama.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        drama.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      result = result.filter(drama => drama.category === selectedCategory);
    }

    setFilteredDramas(result);
  }, [dramas, searchTerm, selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dramasResponse, categoriesResponse, statsResponse] = await Promise.all([
        fetch('/api/v2/dramas'),
        fetch('/api/v2/dramas/categories'),
        fetch('/api/v2/dramas/statistics')
      ]);

      if (dramasResponse.ok) {
        const dramasData = await dramasResponse.json();
        setDramas(dramasData.data);
      }

      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData.data);
      }

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStatistics(statsData.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('获取数据失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // 如果是编辑模式，直接将文件上传到对应剧集的文件夹
      if (editingDrama) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`/api/v2/dramas/${editingDrama.id}/upload`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            [fieldName]: data.filePath
          }));
          showSnackbar('文件上传成功');
        } else {
          const errorData = await response.json();
          showSnackbar(errorData.message || '文件上传失败', 'error');
        }
      } else {
        // 如果是新增模式，先上传到临时文件夹（但新增时不显示海报选择）
        showSnackbar('新增剧集时不支持上传海报', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar('文件上传失败', 'error');
    }
  };

  const handleEpisodeFileUpload = async (episodeIndex, fieldName, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      // 对于剧集文件，我们直接使用编辑模式的上传方式（如果是编辑模式）
      let response;
      if (editingDrama) {
        // 编辑模式：直接上传到剧集文件夹的 episodes 子文件夹
        response = await fetch(`/api/v2/dramas/${editingDrama.id}/upload`, {
          method: 'POST',
          body: uploadFormData
        });
      } else {
        // 新增模式：先上传到临时文件夹
        response = await fetch('/api/v2/dramas/upload', {
          method: 'POST',
          body: uploadFormData
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const updatedEpisodes = [...formData.episodes];
          updatedEpisodes[episodeIndex] = {
            ...updatedEpisodes[episodeIndex],
            [fieldName]: data.filePath
          };
          
          // 如果是视频文件，检查是否有缩略图返回
          if (fieldName === 'videoUrl' && data.thumbnailPath) {
            updatedEpisodes[episodeIndex].image_url = data.thumbnailPath;
            console.log('Video thumbnail automatically generated:', data.thumbnailPath);
          } else if (fieldName === 'videoUrl') {
            // 如果没有返回缩略图，尝试生成一个默认的
            console.log('No thumbnail generated, using default');
          }
          
          setFormData(prev => ({
            ...prev,
            episodes: updatedEpisodes
          }));
          showSnackbar('文件上传成功');
        } else {
          showSnackbar(data.error || '文件上传失败', 'error');
        }
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || '文件上传失败', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar('文件上传失败', 'error');
    }
  };

  // 从视频生成封面（使用视频第一帧）
  const generateVideoCover = async (videoUrl) => {
    try {
      // 由于跨域限制，我们可以使用视频文件的URL作为封面（或者使用默认封面）
      // 这里我们创建一个简单的默认封面
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // 创建视频元素
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.src = videoUrl;
      
      return new Promise((resolve) => {
        video.addEventListener('loadedmetadata', () => {
          // 设置画布大小与视频相同
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          // 播放视频并在第一帧时截图
          video.play();
          
          // 视频播放一小段时间后截图
          setTimeout(() => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            // 转换为 data URL
            const coverDataUrl = canvas.toDataURL('image/png');
            resolve(coverDataUrl);
          }, 100);
        });
        
        // 处理视频加载失败的情况
        video.addEventListener('error', () => {
          // 视频加载失败时使用默认封面
          resolve('');
        });
      });
    } catch (error) {
      console.error('Error generating video cover:', error);
      return ''; // 使用空字符串作为默认值
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleAddDrama = () => {
    setEditingDrama(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      poster: '',
      episodes: []
    });
    setDialogOpen(true);
  };

  const handleEditDrama = (drama) => {
    console.log('=== handleEditDrama called ===');
    console.log('drama:', drama);
    
    // 调试：检查 drama 对象的属性
    console.log('drama properties:', Object.keys(drama));
    console.log('drama.episodes:', drama.episodes);
    console.log('drama.episodes type:', typeof drama.episodes);
    console.log('drama.episodes length:', drama.episodes ? drama.episodes.length : 'undefined');
    
    setEditingDrama(drama);
    const newFormData = {
      title: drama.name,
      description: drama.description,
      category: drama.category,
      poster: drama.poster,
      episodes: drama.episodes.map(episode => ({
        title: episode.title,
        videoUrl: episode.video_url,
        cover: episode.image_url
      }))
    };
    console.log('newFormData:', newFormData);
    setFormData(newFormData);
    console.log('Before setDialogOpen');
    setDialogOpen(true);
    console.log('After setDialogOpen, dialogOpen:', dialogOpen);
    console.log('=== handleEditDrama completed ===');
  };

  const handleDeleteDrama = (drama) => {
    setDramaToDelete(drama);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!dramaToDelete) return;

    try {
      const response = await fetch(`/api/v2/dramas/${dramaToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showSnackbar('剧集删除成功');
        fetchData();
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.message || '删除失败', 'error');
      }
    } catch (error) {
      console.error('Error deleting drama:', error);
      showSnackbar('删除失败', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setDramaToDelete(null);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingDrama ? `/api/v2/dramas/${editingDrama.id}` : '/api/v2/dramas';
      const method = editingDrama ? 'PUT' : 'POST';

      const requestData = {
        name: formData.title,
        description: formData.description,
        category: formData.category
      };

      // 只有在编辑时才包含海报和剧集
      if (editingDrama) {
        if (formData.poster) {
          requestData.poster = formData.poster;
        }
        if (formData.episodes.length > 0) {
          // 转换字段名以匹配后端期望的格式
          requestData.episodes = formData.episodes.map(episode => (
            {
              title: episode.title,
              video_url: episode.videoUrl,
              image_url: episode.cover
            }
          ));
        }
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        showSnackbar(editingDrama ? '剧集更新成功' : '剧集创建成功');
        // 使用 setTimeout 延迟关闭对话框，确保状态更新
        setTimeout(() => {
          setDialogOpen(false);
          fetchData();
        }, 100);
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.message || '操作失败', 'error');
      }
    } catch (error) {
      console.error('Error saving drama:', error);
      showSnackbar('网络请求失败，请检查网络连接', 'error');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddEpisode = () => {
    const newEpisode = {
      id: `episode_${Date.now()}`,
      title: '',
      videoUrl: '',
      cover: ''
    };
    setFormData(prev => ({
      ...prev,
      episodes: [...prev.episodes, newEpisode]
    }));
  };

  const handleRemoveEpisode = (index) => {
    setFormData(prev => ({
      ...prev,
      episodes: prev.episodes.filter((_, i) => i !== index)
    }));
  };

  const handleEpisodeChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      episodes: prev.episodes.map((episode, i) => 
        i === index ? { ...episode, [field]: value } : episode
      )
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography ml={2}>加载中...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {statistics.total_dramas}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                总剧集数
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {statistics.total_episodes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                总集数
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {Object.keys(statistics.categories || {}).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                分类数
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {statistics.average_episodes_per_drama}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                平均每剧集数
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Control Panel */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="搜索剧集标题或描述"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>分类筛选</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="分类筛选"
              >
                <MenuItem value="">所有分类</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.name} value={category.name}>
                    {category.name} ({category.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
              >
                刷新
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddDrama}
              >
                新增剧集
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Drama List */}
      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>海报</TableCell>
                <TableCell>名称</TableCell>
                <TableCell>分类</TableCell>
                <TableCell align="center">播放</TableCell>
                <TableCell align="center">收藏</TableCell>
                <TableCell>集数</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDramas.map(drama => (
                <TableRow key={drama.id}>
                  <TableCell>
                    <img
                      src={drama.poster}
                      alt={drama.name}
                      style={{ width: 80, height: 120, objectFit: 'cover' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">{drama.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{drama.category}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">{drama.view_count || 0}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">{drama.favorite_count || 0}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {drama.episodes.length} 集
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          console.log('Edit button DOM element:', e.currentTarget);
                          console.log('Edit button clicked! Event:', e);
                          console.log('drama:', drama);
                          handleEditDrama(drama);
                        }}
                        title="编辑"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDrama(drama)}
                        title="删除"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredDramas.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              {searchTerm || selectedCategory ? '没有找到匹配的剧集' : '暂无剧集数据'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Drama Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {console.log('Dialog rendered with open:', dialogOpen)}
        <DialogTitle>
          {editingDrama ? '编辑剧集' : '新增剧集'}
        </DialogTitle>
        {/* 调试信息 */}
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Debug: editingDrama = {JSON.stringify(editingDrama)}
          </Typography>
        </DialogContent>
        <form onSubmit={handleFormSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="剧集标题"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="剧集描述"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>分类</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    label="分类"
                  >
                    {categories.map(category => (
                      <MenuItem key={category.name} value={category.name}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            {/* 只有在编辑时才显示海报选择 */}
              {editingDrama && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="body2" color="text.secondary">海报</Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <Button
                        variant="outlined"
                        component="label"
                      >
                        选择文件
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'poster')}
                        />
                      </Button>
                      {formData.poster && (
                        <Box display="flex" alignItems="center">
                          <img
                            src={formData.poster}
                            alt="海报预览"
                            style={{ width: 50, height: 75, objectFit: 'cover', marginRight: 8 }}
                          />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {formData.poster}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* 只有在编辑时才显示剧集列表 */}
              {editingDrama && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">剧集列表</Typography>
                    <Button size="small" onClick={handleAddEpisode}>
                      添加剧集
                    </Button>
                  </Box>

                  {formData.episodes.map((episode, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="剧集标题"
                            value={episode.title}
                            onChange={(e) => handleEpisodeChange(index, 'title', e.target.value)}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="body2" color="text.secondary">视频</Typography>
                            <Box display="flex" gap={1} alignItems="center">
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                              >
                                选择文件
                                <input
                                  type="file"
                                  hidden
                                  accept="video/*"
                                  onChange={(e) => handleEpisodeFileUpload(index, 'videoUrl', e)}
                                />
                              </Button>
                              {episode.videoUrl && (
                                <Typography variant="body2" color="text.secondary" noWrap style={{ maxWidth: 150 }}>
                                  {episode.videoUrl}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={1}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveEpisode(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="body2" color="text.secondary">封面 (自动生成)</Typography>
                            {episode.cover && (
                              <Box display="flex" alignItems="center">
                                <img
                                  src={episode.cover}
                                  alt="封面预览"
                                  style={{ width: 40, height: 30, objectFit: 'cover', marginRight: 8 }}
                                />
                                <Typography variant="body2" color="text.secondary" noWrap style={{ maxWidth: 150 }}>
                                  自动生成的封面
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {formData.episodes.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      暂无剧集，请添加至少一个剧集
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>取消</Button>
            <Button type="submit" variant="contained">
              {editingDrama ? '保存修改' : '创建剧集'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>删除确认</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除剧集 "{dramaToDelete?.title}" 吗？此操作无法撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteConfirmed} color="error">
            确认删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DramaManagementPageV2;
